import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  Home,
  Bath,
  ChefHat,
  HardHat,
  Hammer,
  DoorOpen,
  Trees,
  ArrowRight,
} from 'lucide-react';
import PageLayout from '../PageLayout';
import Seo from '../Seo';
import CTABlock from '../CTABlock';
import { graph, businessSchema, breadcrumbSchema } from '../../seo/site';
import '../ServicePage.css';

const SERVICES = [
  { icon: Building2, name: 'Home Extensions', path: '/extensions', desc: 'Single and double-storey rear extensions, side returns, wraparounds. Fixed-price quotes after a free survey; Havering Council planning managed by us. Typically £28,000 to £55,000 for a single-storey rear.' },
  { icon: Home, name: 'Loft Conversions', path: '/loft-conversions', desc: 'Dormer, hip-to-gable, Velux and mansard conversions. Most Havering properties fall under permitted development. Structural survey included.' },
  { icon: Bath, name: 'Bathroom Refurbishments', path: '/bathroom-refurbishments', desc: 'Full bathroom refits, en-suites, wet rooms and shower installations. Supply and fit, or fit-only.' },
  { icon: ChefHat, name: 'Kitchen Refurbishments', path: '/kitchen-refurbishments', desc: 'Kitchen fit-outs and refurbishments from design to handover, at a written fixed price.' },
  { icon: HardHat, name: 'Roofing', path: '/roofing', desc: 'Pitched and flat roofing, EPDM, GRP and felt systems, repairs and emergency call-out across Havering. No subcontracted roofers.' },
  { icon: Hammer, name: 'Home Renovations', path: '/home-renovations', desc: 'Full home renovations and refurbishments with one dedicated point of contact from survey to sign-off.' },
  { icon: DoorOpen, name: 'Porches', path: '/porches', desc: 'Porch design and construction across Havering, with permitted development advice and written quotes.' },
  { icon: Trees, name: 'Outbuildings & Garden Rooms', path: '/outbuildings', desc: 'Garden rooms, outbuildings and garages built across Havering and Essex. Free site survey, written quote.' },
];

const BuildingServicesPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Seo
        path="/building-services"
        title="Building Services Havering & Essex | Extensions, Lofts, Roofing | Fallow"
        description="Extensions, loft conversions, bathrooms, kitchens, roofing and renovations across Havering & Essex. The building work behind better air conditioning. Fixed prices, CIS-registered. Call 0203 576 5962."
        jsonLd={graph([
          businessSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Building Services', path: '/building-services' },
          ]),
        ])}
      />

      <PageLayout
        heroTitle="Building Services in Havering & Essex"
        heroSubtitle="The building work behind better air conditioning, and everything else your property needs."
        heroImage="/images/hero-construction.jpg"
      >
        <section className="service-benefits">
          <div className="service-container">
            <motion.div
              className="content-block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p>Every building project is managed by our own team from survey to Building Control sign-off: one dedicated point of contact, fixed written prices, staged payments, and a 12-month defect liability period in writing. It is also why our <Link to="/air-conditioning" className="contact-link">air conditioning installations</Link> finish cleaner than a specialist\'s: the electrics and the making good are already in-house.</p>
            </motion.div>

            <motion.div
              className="benefits-grid-modern"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {SERVICES.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={service.name}
                    className="benefit-card-modern"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.06 * index }}
                    whileHover={{ y: -4 }}
                  >
                    <div className="benefit-icon-wrapper">
                      <Icon size={28} />
                    </div>
                    <h3 className="benefit-title">{service.name}</h3>
                    <p className="benefit-description">{service.desc}</p>
                    <p className="benefit-description" style={{ marginTop: '12px' }}>
                      <Link to={service.path} className="contact-link">
                        View {service.name.toLowerCase()} <ArrowRight size={14} style={{ verticalAlign: 'middle' }} />
                      </Link>
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div
              className="quick-links"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Link to="/air-conditioning" className="btn-secondary">Air Conditioning</Link>
              <Link to="/property-services" className="btn-secondary">Property Services</Link>
              <Link to="/havering" className="btn-secondary">Havering</Link>
              <Link to="/contact" className="btn-secondary">Get a Free Quote</Link>
            </motion.div>
          </div>
        </section>

        <CTABlock />
      </PageLayout>
    </>
  );
};

export default BuildingServicesPage;
